import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Source, Layer } from "react-map-gl";
import BaseMap from "./BaseMap";

const RADIUS_MULTIPLIER = 0.005;
const ALL_SPANS_RADIUS_MULTIPLIER = 0.001;

const calculateEndPoint = (center, angleInDegrees, distance) => {
    const angleInRadians = ((-angleInDegrees + 120) * Math.PI) / 180;
    const x = center[0] + distance * Math.cos(angleInRadians);
    const y = center[1] + distance * Math.sin(angleInRadians);
    return [x, y];
};

const createPieSlices = (spanCenter, coefficients, radiusMultiplier = RADIUS_MULTIPLIER) => {
    const slices = [];
    const firstPoint = calculateEndPoint(spanCenter, 0, coefficients.coefficients["0"] * radiusMultiplier);
    let lastEndPoint = firstPoint;

    for (let angle = 0; angle < 360; angle += 30) {
        const endAngle = angle + 30;
        const coefficient = coefficients.coefficients[angle.toString()];
        const endRadialPoint = calculateEndPoint(spanCenter, endAngle, coefficient * radiusMultiplier);
        const slicePolygon = [spanCenter, lastEndPoint, angle === 330 ? firstPoint : endRadialPoint, spanCenter];

        slices.push({
            type: "Feature",
            properties: {
                wdir: angle,
                coefficient: coefficient,
                description: `Coefficient (${angle}): ${coefficient}`,
            },
            geometry: {
                type: "Polygon",
                coordinates: [slicePolygon],
            },
        });

        lastEndPoint = endRadialPoint;
    }

    return slices;
};

const createCoefficientOneCircle = (spanCenter, radiusMultiplier = RADIUS_MULTIPLIER) => {
    const points = [];

    for (let angle = 0; angle < 360; angle += 30) {
        const point = calculateEndPoint(spanCenter, angle, 1.0 * radiusMultiplier);
        points.push(point);
    }

    points.push(points[0]);

    return {
        type: "Feature",
        properties: {
            description: "This is a reference circle for when the coefficient is 1.0",
        },
        geometry: {
            type: "Polygon",
            coordinates: [points],
        },
    };
};

// Custom functions to ignore NaN and infinite values
// Uses for loop to avoid hitting max call stack with large arrays (e.g. 10k x 10k elevation grids) when using reduces
const findMax = (arr) => {
    let max = -Infinity;
    for (const v of arr) if (!isNaN(v) && isFinite(v)) max = Math.max(max, v);
    return max === -Infinity ? 0 : max;
};

const findMin = (arr) => {
    let min = Infinity;
    for (const v of arr) if (!isNaN(v) && isFinite(v) && v > 0) min = Math.min(min, v);
    return min === Infinity ? 0 : min;
};

function renderGridToCanvas(data, width, height, colorFn) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.createImageData(width, height);
    const pixels = imageData.data;

    for (let i = 0; i < width * height; i++) {
        const value = data[i];
        const px = i * 4;
        if (isNaN(value) || value <= 0) {
            pixels[px + 3] = 0;
        } else {
            const [r, g, b, a] = colorFn(value);
            pixels[px] = r;
            pixels[px + 1] = g;
            pixels[px + 2] = b;
            pixels[px + 3] = a;
        }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
}

export function parseTerrainData(json) {
    const { bounds, width, height, ground_elevation, foliage_height, building_height } = json;

    const elevationMax = findMax(ground_elevation);
    const elevationMin = findMin(ground_elevation);
    const elevationRange = elevationMax - elevationMin || 1;
    const elevationColorAt = (v) => {
        const t = Math.min(1, Math.max(0, (v - elevationMin) / elevationRange));
        return [Math.round(100 + 120 * t), Math.round(70 + 80 * t), Math.round(40 + 40 * t), Math.round(180 * t + 60)];
    };
    const elevationImageUrl = renderGridToCanvas(ground_elevation, width, height, elevationColorAt);
    const [rMin, gMin, bMin, aMin] = elevationColorAt(elevationMin);
    const [rMax, gMax, bMax, aMax] = elevationColorAt(elevationMax);
    const elevationGradient = `linear-gradient(to top, rgba(${rMin},${gMin},${bMin},${(aMin / 255).toFixed(
        2
    )}), rgba(${rMax},${gMax},${bMax},${(aMax / 255).toFixed(2)}))`;

    const foliageMax = findMax(foliage_height);

    const foliageImageUrl = renderGridToCanvas(foliage_height, width, height, (v) => {
        const t = Math.min(1, v / foliageMax);
        return [34, Math.round(85 + 170 * t), 34, Math.round(220 * t)];
    });

    const buildingsImageUrl = renderGridToCanvas(building_height, width, height, () => [150, 150, 170, 180]);

    return {
        foliage: { imageUrl: foliageImageUrl, bounds, max: foliageMax },
        buildings: { imageUrl: buildingsImageUrl, bounds },
        elevation: {
            imageUrl: elevationImageUrl,
            bounds,
            max: elevationMax,
            min: elevationMin,
            gradient: elevationGradient,
        },
        raw: { ground_elevation, foliage_height, building_height, bounds, width, height },
    };
}

const PALM_INTERACTIVE_LAYER_IDS = [
    "coefficient-circle-layer",
    "coefficient-slices-layer",
    "structures-layer",
    "polyline-layer",
];

export default function PalmMap({
    simulationAreas,
    simulationSpans,
    coefficients,
    allSpanCoefficients,
    center,
    zoom,
    spanCircleCenter,
    terrainData,
    selectedAreaKey,
    additionalInteractiveLayerIds = [],
    children,
}) {
    const mapRef = useRef();
    const baseCenter = { longitude: center.lng ?? center.longitude, latitude: center.lat ?? center.latitude };
    const [structureGeoJSON, setStructureGeoJSON] = useState(null);
    const [elevationVisible, setElevationVisible] = useState(false);
    const [foliageVisible, setFoliageVisible] = useState(false);
    const [buildingsVisible, setBuildingsVisible] = useState(false);
    const [selectedTerrainArea, setSelectedTerrainArea] = useState(null);
    const [terrainHover, setTerrainHover] = useState(null);

    const parsedTerrain = useMemo(() => {
        if (!terrainData) return null;
        return Object.entries(terrainData).map(([areaKey, viz]) => ({ areaKey, ...parseTerrainData(viz) }));
    }, [terrainData]);

    useEffect(() => {
        if (!parsedTerrain) return;
        if (selectedAreaKey) {
            setSelectedTerrainArea(selectedAreaKey);
        } else {
            setSelectedTerrainArea((prev) => prev ?? parsedTerrain[0]?.areaKey ?? null);
        }
    }, [selectedAreaKey, parsedTerrain]);
    const [circleGeoJSON, setCircleGeoJSON] = useState(null);
    const [rectangularAreasGeoJSON, setRectangularAreasGeoJSON] = useState(null);
    const [slicesGeoJSON, setSlicesGeoJSON] = useState(null);
    const [polylineGeoJSON, setPolylineGeoJSON] = useState(null);

    const handleTerrainMouseMove = useCallback(
        (event) => {
            const anyVisible = elevationVisible || foliageVisible || buildingsVisible;
            if (!anyVisible || !parsedTerrain) {
                setTerrainHover(null);
                return;
            }
            const terrain = parsedTerrain.find(({ areaKey }) => areaKey === selectedTerrainArea);
            if (!terrain) {
                setTerrainHover(null);
                return;
            }
            const { raw } = terrain;
            const { ground_elevation, foliage_height, building_height, bounds, width, height } = raw;
            const { lngLat, point } = event;
            const west = bounds[0][0];
            const north = bounds[0][1];
            const east = bounds[1][0];
            const south = bounds[2][1];
            if (lngLat.lng < west || lngLat.lng > east || lngLat.lat < south || lngLat.lat > north) {
                setTerrainHover(null);
                return;
            }
            const col = Math.min(Math.floor(((lngLat.lng - west) / (east - west)) * width), width - 1);
            const row = Math.min(Math.floor(((north - lngLat.lat) / (north - south)) * height), height - 1);
            const idx = row * width + col;
            const elev = ground_elevation[idx];
            const fol = foliage_height[idx];
            const bld = building_height[idx];
            setTerrainHover({
                x: point.x,
                y: point.y,
                elevation: elevationVisible && elev > 0 && !isNaN(elev) ? elev : null,
                foliage: foliageVisible && fol > 0 && !isNaN(fol) ? fol : null,
                building: buildingsVisible && bld > 0 && !isNaN(bld) ? bld : null,
            });
        },
        [elevationVisible, foliageVisible, buildingsVisible, parsedTerrain, selectedTerrainArea]
    );

    const handleTerrainMouseLeave = useCallback(() => setTerrainHover(null), []);

    const onClick = useCallback((event) => {
        const feature = event.features && event.features[0];
        if (!feature || !mapRef.current) return;

        const geom = feature.geometry;
        let lng, lat;

        if (geom.type === "Point") {
            [lng, lat] = geom.coordinates;
        } else if (geom.type === "LineString") {
            // Use the stored midpoint stored in properties (avoids recomputing)
            lng = feature.properties.mid_longitude;
            lat = feature.properties.mid_latitude;
        } else if (geom.type === "Polygon") {
            // Use the centroid of the first ring
            const ring = geom.coordinates[0];
            lng = ring.reduce((sum, c) => sum + c[0], 0) / ring.length;
            lat = ring.reduce((sum, c) => sum + c[1], 0) / ring.length;
        }

        if (lng !== undefined && lat !== undefined) {
            mapRef.current.flyTo({ longitude: lng, latitude: lat });
        }
    }, []);

    useEffect(() => {
        setRectangularAreasGeoJSON({
            type: "FeatureCollection",
            features: simulationAreas.simulation_areas.map((area, idx) => ({
                type: "Feature",
                geometry: {
                    id: idx,
                    type: "Polygon",
                    coordinates: [
                        [
                            [+area.bbox.min_longitude, +area.bbox.min_latitude],
                            [+area.bbox.min_longitude, +area.bbox.max_latitude],
                            [+area.bbox.max_longitude, +area.bbox.max_latitude],
                            [+area.bbox.max_longitude, +area.bbox.min_latitude],
                            [+area.bbox.min_longitude, +area.bbox.min_latitude],
                        ],
                    ],
                },
            })),
        });

        setPolylineGeoJSON({
            type: "FeatureCollection",
            features: simulationAreas.simulation_areas.flatMap((area) => {
                return area.spans.flatMap((spanString) => {
                    const span = simulationSpans.spans.find((s) => s.name === spanString);
                    if (!span) return [];
                    const startStructure = span.start_structure;
                    const endStructure = span.end_structure;

                    return [
                        {
                            type: "Feature",
                            properties: {
                                description: `Span: ${spanString}`,
                                mid_longitude: (+startStructure.longitude + +endStructure.longitude) / 2,
                                mid_latitude: (+startStructure.latitude + +endStructure.latitude) / 2,
                            },
                            geometry: {
                                type: "LineString",
                                coordinates: [
                                    [startStructure.longitude, startStructure.latitude],
                                    [endStructure.longitude, endStructure.latitude],
                                ],
                            },
                        },
                    ];
                });
            }),
        });

        const uniqueCoords = new Set();
        const uniqueFeatures = simulationSpans.spans.flatMap((span) => {
            const features = [];
            const startKey = `${span.start_structure.latitude},${span.start_structure.longitude}`;
            const endKey = `${span.end_structure.latitude},${span.end_structure.longitude}`;
            if (!uniqueCoords.has(startKey)) {
                uniqueCoords.add(startKey);
                features.push({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [span.start_structure.longitude, span.start_structure.latitude],
                    },
                    properties: {
                        description: `Structure: ${span.start_structure.name}`,
                    },
                });
            }
            if (!uniqueCoords.has(endKey)) {
                uniqueCoords.add(endKey);
                features.push({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [span.end_structure.longitude, span.end_structure.latitude],
                    },
                    properties: {
                        description: `Structure: ${span.end_structure.name}`,
                    },
                });
            }
            return features;
        });

        setStructureGeoJSON({
            type: "FeatureCollection",
            features: uniqueFeatures,
        });
    }, [simulationAreas, simulationSpans]);

    useEffect(() => {
        if (coefficients !== undefined && spanCircleCenter) {
            const spanCenter = [spanCircleCenter.longitude, spanCircleCenter.latitude];
            setSlicesGeoJSON({
                type: "FeatureCollection",
                features: createPieSlices(spanCenter, coefficients),
            });
            setCircleGeoJSON({
                type: "FeatureCollection",
                features: [createCoefficientOneCircle(spanCenter)],
            });
        }
    }, [coefficients]);

    // Show all span pies when coefficients are loaded but no specific span is selected
    useEffect(() => {
        if (!allSpanCoefficients || !simulationSpans || spanCircleCenter) return;

        const allSlices = [];
        const allCircles = [];
        for (const coeff of allSpanCoefficients) {
            const span = simulationSpans.spans.find((s) => s.name === coeff.span_id);
            if (!span) continue;
            const center = [
                (+span.start_structure.longitude + +span.end_structure.longitude) / 2,
                (+span.start_structure.latitude + +span.end_structure.latitude) / 2,
            ];
            allSlices.push(...createPieSlices(center, coeff, ALL_SPANS_RADIUS_MULTIPLIER));
            allCircles.push(createCoefficientOneCircle(center, ALL_SPANS_RADIUS_MULTIPLIER));
        }
        setSlicesGeoJSON({ type: "FeatureCollection", features: allSlices });
        setCircleGeoJSON({ type: "FeatureCollection", features: allCircles });
    }, [allSpanCoefficients, simulationSpans, spanCircleCenter]);

    useEffect(() => {
        if (coefficients === undefined && spanCircleCenter) {
            setCircleGeoJSON({
                type: "FeatureCollection",
                features: spanCircleCenter
                    ? [
                          {
                              type: "Feature",
                              geometry: { type: "Point", coordinates: [spanCircleCenter.longitude, spanCircleCenter.latitude] },
                          },
                      ]
                    : [],
            });
        }
    }, [spanCircleCenter]);

    return (
        <BaseMap
            ref={mapRef}
            center={baseCenter}
            zoom={zoom}
            interactiveLayerIds={[...PALM_INTERACTIVE_LAYER_IDS, ...additionalInteractiveLayerIds]}
            onClick={onClick}
            onMouseMove={handleTerrainMouseMove}
            onMouseLeave={handleTerrainMouseLeave}
            style={{ height: "750px", flex: 1 }}
        >
            {/* Simulation area rectangles — always visible */}
            <Source key="rectangle-data" id="rectangle-data" type="geojson" data={rectangularAreasGeoJSON}>
                <Layer
                    type="fill"
                    paint={{
                        "fill-color": "#0000FF",
                        "fill-opacity": 0.15,
                    }}
                />
            </Source>
            {/* Selected span highlight (no coefficients) */}
            {coefficients === undefined && spanCircleCenter && (
                <Source key="circle-data" id="circle-data" type="geojson" data={circleGeoJSON}>
                    <Layer
                        type="circle"
                        paint={{
                            "circle-radius": 25,
                            "circle-color": "#FFA500",
                            "circle-opacity": 0.6,
                        }}
                    />
                </Source>
            )}

            {/* Coefficient pie slices (layered on top of rectangles when coefficients selected) */}
            {(coefficients !== undefined || allSpanCoefficients) && (
                <>
                    {/* Reference circle at coefficient = 1.0 — shown as outline only */}
                    <Source key="coefficient-circle" id="coefficient-circle" type="geojson" data={circleGeoJSON}>
                        <Layer
                            id="coefficient-circle-layer"
                            type="line"
                            paint={{
                                "line-color": "#888",
                                "line-width": 1.5,
                                "line-dasharray": [3, 2],
                            }}
                        />
                    </Source>
                    {/* Coefficient slices — color-coded low→high by value */}
                    <Source key="coefficient-slices" id="coefficient-slices" type="geojson" data={slicesGeoJSON}>
                        <Layer
                            id="coefficient-slices-layer"
                            type="fill"
                            paint={{
                                "fill-color": [
                                    "interpolate",
                                    ["linear"],
                                    ["get", "coefficient"],
                                    0.0, "#FFFDE7",
                                    0.3, "#FFF176",
                                    0.6, "#FFCA28",
                                    0.8, "#FFA000",
                                    0.9, "#FF6F00",
                                    1.0, "#E65100",
                                    1.2, "#BF360C",
                                ],
                                "fill-opacity": 0.75,
                            }}
                        />
                    </Source>
                </>
            )}
            <Source id="polyline-data" type="geojson" data={polylineGeoJSON}>
                <Layer
                    id="polyline-layer"
                    type="line"
                    paint={{
                        "line-color": "#FF0000",
                        "line-width": 2,
                    }}
                />
            </Source>
            <Source id="structures-source" type="geojson" data={structureGeoJSON}>
                <Layer
                    id="structures-layer"
                    type="symbol"
                    paint={{
                        "text-color": "#FF0000",
                    }}
                    layout={{
                        "text-field": "X",
                        "text-size": 20,
                    }}
                />
            </Source>
            {parsedTerrain &&
                parsedTerrain
                    .filter(({ areaKey }) => areaKey === selectedTerrainArea)
                    .flatMap(({ areaKey, elevation, foliage, buildings }) => {
                        const layers = [];
                        if (elevationVisible) {
                            layers.push(
                                <Source
                                    key={`terrain-elevation-${areaKey}`}
                                    id={`terrain-elevation-${areaKey}`}
                                    type="image"
                                    url={elevation.imageUrl}
                                    coordinates={elevation.bounds}
                                >
                                    <Layer type="raster" paint={{ "raster-opacity": 0.7 }} />
                                </Source>
                            );
                        }
                        if (foliageVisible) {
                            layers.push(
                                <Source
                                    key={`terrain-foliage-${areaKey}`}
                                    id={`terrain-foliage-${areaKey}`}
                                    type="image"
                                    url={foliage.imageUrl}
                                    coordinates={foliage.bounds}
                                >
                                    <Layer type="raster" paint={{ "raster-opacity": 0.75 }} />
                                </Source>
                            );
                        }
                        if (buildingsVisible) {
                            layers.push(
                                <Source
                                    key={`terrain-buildings-${areaKey}`}
                                    id={`terrain-buildings-${areaKey}`}
                                    type="image"
                                    url={buildings.imageUrl}
                                    coordinates={buildings.bounds}
                                >
                                    <Layer type="raster" paint={{ "raster-opacity": 0.8 }} />
                                </Source>
                            );
                        }
                        return layers;
                    })}
            {parsedTerrain && (
                <div
                    style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 4,
                        backgroundColor: "rgba(255,255,255,0.92)",
                        borderRadius: 4,
                        padding: "8px 12px",
                        fontSize: 12,
                        minWidth: 130,
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Terrain Layers</div>
                    {[
                        ["Elevation", elevationVisible, setElevationVisible],
                        ["Foliage", foliageVisible, setFoliageVisible],
                        ["Buildings", buildingsVisible, setBuildingsVisible],
                    ].map(([label, visible, setVisible]) => (
                        <label key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <input
                                type="checkbox"
                                checked={visible}
                                onChange={(e) => setVisible(e.target.checked)}
                                style={{ cursor: "pointer" }}
                            />
                            {label}
                        </label>
                    ))}
                    {elevationVisible && parsedTerrain[0]?.elevation && (
                        <div style={{ marginTop: 8, borderTop: "1px solid #ddd", paddingTop: 6 }}>
                            <div style={{ marginBottom: 3 }}>Elevation (m)</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span>{Math.round(parsedTerrain[0].elevation.min)}</span>
                                <div
                                    style={{
                                        flex: 1,
                                        height: 10,
                                        background: parsedTerrain[0].elevation.gradient,
                                        border: "1px solid #ccc",
                                        borderRadius: 2,
                                    }}
                                />
                                <span>{Math.round(parsedTerrain[0].elevation.max)}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {terrainHover &&
                (terrainHover.elevation != null || terrainHover.foliage != null || terrainHover.building != null) && (
                    <div
                        style={{
                            position: "absolute",
                            zIndex: 10,
                            pointerEvents: "none",
                            left: terrainHover.x + 12,
                            top: terrainHover.y + 12,
                            backgroundColor: "rgba(0,0,0,0.75)",
                            color: "#fff",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 12,
                            whiteSpace: "pre",
                        }}
                    >
                        {[
                            terrainHover.elevation != null && `Elevation: ${terrainHover.elevation.toFixed(1)} m`,
                            terrainHover.foliage != null && `Foliage: ${terrainHover.foliage.toFixed(1)} m`,
                            terrainHover.building != null && `Building: ${terrainHover.building.toFixed(1)} m`,
                        ]
                            .filter(Boolean)
                            .join("\n")}
                    </div>
                )}
            {children}
        </BaseMap>
    );
}
